import { Block, PaginationComponent } from "../Component";
import Icon from "../icon/Icon";
import ListSkeletonLoading from "../skeleton/ListSkeletonLoading";
import { DataTableHead, DataTableRow } from "./DataTable";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import "./AppTable.css";

/// Shared paginated list table.
///
/// columns: [{ head, cell(item), headClassName?, cellClassName?, onClick? }]
/// onReorder({ item, from, to }): makes rows draggable (a grip column is
/// added in front); called when a row is dropped at a new index.
/// All table styling lives in AppTable.css under `.app-table`, so the look can
/// be changed (or the table swapped out) in one place.
export default function BasePaginationTable({
  loading,
  list,
  columns,
  limit,
  page,
  onRowClick,
  totalItems,
  onPageChange,
  hasNextPage,
  onReorder,
  emptyText = "No data found",
}) {
  const perPage = limit ?? 10;
  const currentPage = page ?? 1;
  const isEmpty = !loading && list && list.length === 0;
  const showPagination = !isEmpty && !(currentPage === 1 && !hasNextPage);
  const from = perPage * (currentPage - 1) + 1;
  const to = Math.min(perPage * currentPage, totalItems ?? 0);

  const allColumns = onReorder
    ? [
        {
          head: null,
          headClassName: "app-table__grip-col",
          cellClassName: "app-table__grip-col",
          cell: () => <Icon name="view-row-wd" className="app-table__grip" />,
        },
        ...columns,
      ]
    : columns;

  const renderCells = (item) =>
    allColumns.map((column, index) => (
      <DataTableRow
        key={index}
        onClick={column.onClick ? () => column.onClick(item) : null}
        className={column.cellClassName}
      >
        {column.cell(item)}
      </DataTableRow>
    ));

  const rowClass = (dragging) =>
    [
      "nk-tb-item",
      onRowClick ? "is-clickable" : "",
      onReorder ? "is-draggable" : "",
      dragging ? "is-dragging" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const renderRows = () => {
    if (loading) {
      return <ListSkeletonLoading limit={perPage} columns={allColumns} />;
    }
    return list?.map((item, index) =>
      onReorder ? (
        <Draggable
          key={item._id ?? index}
          draggableId={String(item._id ?? index)}
          index={index}
        >
          {(provided, snapshot) => (
            <div
              className={rowClass(snapshot.isDragging)}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              onClick={onRowClick ? () => onRowClick(item) : null}
            >
              {renderCells(item)}
            </div>
          )}
        </Draggable>
      ) : (
        <div
          key={item?._id ?? index}
          className={rowClass(false)}
          onClick={onRowClick ? () => onRowClick(item) : null}
        >
          {renderCells(item)}
        </div>
      ),
    );
  };

  const head = (
    <DataTableHead>
      {allColumns.map((column, index) => (
        <DataTableRow key={index} className={column.headClassName}>
          {column.head}
        </DataTableRow>
      ))}
    </DataTableHead>
  );

  return (
    <Block>
      <div className="app-table">
        <div className="app-table__scroll">
          {onReorder ? (
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                const fromIndex = result.source.index;
                const toIndex = result.destination.index;
                if (fromIndex === toIndex) return;
                onReorder({
                  item: list[fromIndex],
                  from: fromIndex,
                  to: toIndex,
                });
              }}
            >
              <Droppable droppableId="app-table-rows">
                {(provided) => (
                  <div
                    className="nk-tb-list"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {head}
                    {renderRows()}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className="nk-tb-list">
              {head}
              {renderRows()}
            </div>
          )}
        </div>

        {isEmpty && (
          <div className="app-table__empty">
            <span className="app-table__empty-icon">
              <Icon name="inbox"></Icon>
            </span>
            <span>{emptyText}</span>
          </div>
        )}

        {showPagination && (
          <div className="app-table__footer">
            <span className="app-table__info">
              {totalItems
                ? `Showing ${from}–${to} of ${totalItems}`
                : `Page ${currentPage}`}
            </span>
            <PaginationComponent
              totalItems={totalItems}
              itemPerPage={perPage}
              paginate={onPageChange}
              currentPage={currentPage}
            />
          </div>
        )}
      </div>
    </Block>
  );
}
